import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { InfoCircleOutlined, UpOutlined, DownOutlined } from '@ant-design/icons'
import AutomatedTrafficFineNotificationVehicleItem from '../automatedTrafficFineNotificationVehicleItem'
import LoadingPopup from '../../../../components/LoadingPopup'
import { validatorPlateNumber, sanitizePlateInput } from '../../../../helper/validatorPlateNumber'
import './index.scss'
import AutomatedTrafficFineNotificationServiceVehicleType from '../automatedTrafficFineNotificationServiceVehicleType'
import vehicleCar from '../../../../assets/images/vehicle_car.png'
import vehicleElectricMotorbike from '../../../../assets/images/vehicle_electric_motorbike.png'
import vehicleMotorbike from '../../../../assets/images/vehicle_motorbike.png'

const DEFAULT_VISIBLE = 3

const optionsVehicleType = [
  { vehicleType: 30, label: 'Xe máy', value: 30, iconUrl: vehicleMotorbike },
  { vehicleType: 1, label: 'Ô tô', value: 1, iconUrl: vehicleCar },
  { vehicleType: 40, label: 'Xe máy điện', value: 40, iconUrl: vehicleElectricMotorbike }
]

export default function AutomatedTrafficFineNotificationVehicleList({
  vehicles,
  selectedVehicles,
  selectedServices,
  maxVehicles,
  phoneNumber,
  isSingleMode,
  isLoading,
  onSelectVehicle,
  onDeleteVehicle,
  onVehiclesChange
}) {
  // Component state
  const [plateNumber, setPlateNumber] = useState('')
  const [plateError, setPlateError] = useState('')

  const [isAddingVehicle, setIsAddingVehicle] = useState(false)
  const [expanded, setExpanded] = useState(false)

  const [selectedVehicleType, setSelectedVehicleType] = useState(selectedServices[0]?.vehicleType || optionsVehicleType[0].vehicleType)

  useEffect(() => {
    setSelectedVehicleType(selectedServices[0]?.vehicleType || optionsVehicleType[0].vehicleType)
  }, [selectedServices])

  // Calculate remaining vehicles to select
  const canToggle = vehicles.length > DEFAULT_VISIBLE
  const visibleVehicles = expanded ? vehicles : vehicles.slice(0, DEFAULT_VISIBLE)

  // Handle plate number input change (add vehicle only)
  const handlePlateChange = (e) => {
    const value = sanitizePlateInput(e.target.value)
    setPlateNumber(value)
    setPlateError('')
    // Không validate real-time, chỉ clear error
  }

  // Handle add vehicle
  const handleAddVehicle = async () => {
    if (!plateNumber || isAddingVehicle) return

    setIsAddingVehicle(true)
    setPlateError('')

    try {
      // Validate biển số
      await validatorPlateNumber(plateNumber)

      // Kiểm tra trùng lặp trong danh sách hiện tại
      const normalizedPlate = plateNumber
        .toUpperCase()
        .replace(/Đ/g, 'D')
        .replace(/[^0-9A-Z]/g, '')
      const isDuplicate = vehicles.some(
        (vehicle) =>
          vehicle.plate
            .toUpperCase()
            .replace(/Đ/g, 'D')
            .replace(/[^0-9A-Z]/g, '') === normalizedPlate && vehicle.vehicleType === selectedVehicleType
      )

      if (isDuplicate) {
        setPlateError('Xe đã có trong danh sách')
        return
      }

      // Gọi API thêm xe
      const result = await onVehiclesChange(plateNumber, selectedVehicleType, phoneNumber)
      if (result.success) {
        // Clear input on success
        setPlateNumber('')
        setPlateError('')
      } else {
        setPlateError('Không thể thêm xe. Vui lòng thử lại sau.')
      }
    } catch (error) {
      setPlateError(error || 'Biển số không hợp lệ')
    } finally {
      setIsAddingVehicle(false)
    }
  }

  // Check if vehicle is selected
  const isVehicleSelected = (vehicle) => {
    if (isSingleMode) {
      // Single mode: check if vehicle is in selectedVehicles array
      return selectedVehicles.some((sv) => sv.id === vehicle.id && sv.source === 'list')
    } else {
      // Multi mode: check in selectedVehicles array
      return selectedVehicles.some((sv) => sv.id === vehicle.id && sv.source === 'list')
    }
  }

  // Check if vehicle should be disabled
  const shouldDisableVehicle = (vehicle) => {
    // Xe có gói thì ko cho chọn nữa
    if (vehicle?.activeNotifyPackageIndex >= 0 && vehicle?.customerSchedule?.[vehicle.activeNotifyPackageIndex]) {
      return true
    }
    if (selectedServices[0]?.vehicleType && selectedServices[0]?.vehicleType !== vehicle?.vehicleType) {
      return true
    }
    if (isSingleMode) return false
    const isSelected = isVehicleSelected(vehicle)
    return !isSelected && selectedVehicles.length >= maxVehicles
  }

  return (
    <div className="automated-traffic-fine-notification-vehicle-list">
      {/* Input section */}
      <div className="vehicle-info">
        <div className="vehicle-info__title">Thông tin xe</div>
        <AutomatedTrafficFineNotificationServiceVehicleType
          options={optionsVehicleType}
          value={selectedVehicleType}
          onChange={(e) => {
            setSelectedVehicleType(e)
          }}
        />
        <div className={`vehicle-info__input-row ${plateError ? 'vehicle-info__input-row--has-error' : ''}`}>
          <div className="vehicle-info__input-wrapper">
            <input
              className={`vehicle-info__input ${plateError ? 'vehicle-info__input--error' : ''}`}
              type="text"
              value={plateNumber}
              onChange={handlePlateChange}
              placeholder="Ví dụ: 29A12345"
              autoCapitalize="characters"
              inputMode="text"
            />
            {plateError && (
              <span className="vehicle-info__error">
                <InfoCircleOutlined style={{ fontSize: '13px', marginRight: '4px' }} />
                {plateError}
              </span>
            )}
          </div>
          <button className="vehicle-info__add-button" onClick={handleAddVehicle} disabled={!plateNumber || isAddingVehicle}>
            Đăng ký
          </button>
        </div>
      </div>

      {/* Vehicle list section */}
      {vehicles.length > 0 && (
        <div className="vehicle-list">
          <div className="vehicle-list-title">Xe của bạn ({vehicles.length})</div>
          {isLoading ? (
            <LoadingPopup />
          ) : vehicles.length > 0 ? (
            <>
              {visibleVehicles.map((vehicle) => (
                <AutomatedTrafficFineNotificationVehicleItem
                  key={vehicle.id}
                  vehicle={vehicle}
                  selected={isVehicleSelected(vehicle)}
                  disabled={shouldDisableVehicle(vehicle)}
                  onSelect={onSelectVehicle}
                  onDelete={() => {
                    onDeleteVehicle(vehicle)
                  }}
                />
              ))}
              {canToggle && (
                <div className="vehicle-list-toggle" onClick={() => setExpanded(!expanded)}>
                  <span className="vehicle-list-toggle__text">{expanded ? 'Thu gọn' : 'Xem thêm'}</span>
                  {expanded ? <UpOutlined className="vehicle-list-toggle__icon" /> : <DownOutlined className="vehicle-list-toggle__icon" />}
                </div>
              )}
            </>
          ) : null}
        </div>
      )}
    </div>
  )
}

AutomatedTrafficFineNotificationVehicleList.propTypes = {
  vehicles: PropTypes.array.isRequired,
  selectedVehicles: PropTypes.array.isRequired,
  maxVehicles: PropTypes.number.isRequired,
  vehicleType: PropTypes.number.isRequired,
  phoneNumber: PropTypes.string.isRequired,
  isSingleMode: PropTypes.bool,
  isLoading: PropTypes.bool,
  onSelectVehicle: PropTypes.func.isRequired,
  onDeleteVehicle: PropTypes.func.isRequired,
  onVehiclesChange: PropTypes.func.isRequired
}

AutomatedTrafficFineNotificationVehicleList.defaultProps = {
  isSingleMode: false,
  isLoading: false
}
